import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IMaskInput } from 'react-imask'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { apiFetch } from '../../lib/api'
import { useAuth } from '../../state/auth'
import { ConfirmModal } from '../ConfirmModal'

type Employee = {
  id: string
  companyId: string
  fullName: string
  cpfMasked: string
  roleTitle: string
  baseSalary: number
  admissionDateBr: string
  isActive: boolean
}

type Company = {
  id: string
  tradeName: string
  legalName: string
}

const schema = z.object({
  companyId: z.string().min(1, 'Selecione a empresa'),
  fullName: z.string().min(3, 'Informe o nome completo'),
  cpf: z.string().min(14, 'Informe o CPF completo'),
  roleTitle: z.string().min(2, 'Informe o cargo'),
  baseSalary: z.string().min(1, 'Informe o salário base'),
  admissionDateBr: z.string().min(10, 'Informe a data (DD/MM/AAAA)')
})

type FormData = z.input<typeof schema>

function formatSalaryForInput(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function EmployeesPage() {
  const { selectedCompanyId, setSelectedCompanyId } = useAuth()
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [deleteEmployeeConfirm, setDeleteEmployeeConfirm] = useState<Employee | null>(null)

  const companiesQ = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiFetch<{ companies: Company[] }>('/companies')
  })

  const listQ = useQuery({
    queryKey: ['employees', selectedCompanyId],
    enabled: !!selectedCompanyId,
    queryFn: () => apiFetch<{ employees: Employee[] }>(`/companies/${selectedCompanyId}/employees`)
  })

  const employees = listQ.data?.employees ?? []
  const filteredEmployees = useMemo(() => {
    const f = filter.trim().toLowerCase()
    if (!f) return employees
    return employees.filter(
      (e) =>
        e.fullName.toLowerCase().includes(f) ||
        (e.cpfMasked && e.cpfMasked.replace(/\D/g, '').includes(f.replace(/\D/g, ''))) ||
        e.roleTitle.toLowerCase().includes(f)
    )
  }, [employees, filter])

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { companyId: selectedCompanyId ?? '' }
  })

  useEffect(() => {
    if (selectedCompanyId && !editingEmployee) setValue('companyId', selectedCompanyId)
  }, [editingEmployee, selectedCompanyId, setValue])

  useEffect(() => {
    const companies = companiesQ.data?.companies ?? []
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(companies[0]!.id)
      setValue('companyId', companies[0]!.id)
    }
  }, [companiesQ.data?.companies, selectedCompanyId, setSelectedCompanyId, setValue])

  useEffect(() => {
    if (editingEmployee) {
      reset({
        companyId: editingEmployee.companyId,
        fullName: editingEmployee.fullName,
        cpf: editingEmployee.cpfMasked,
        roleTitle: editingEmployee.roleTitle,
        baseSalary: formatSalaryForInput(editingEmployee.baseSalary),
        admissionDateBr: editingEmployee.admissionDateBr
      })
    }
  }, [editingEmployee, reset])

  const onSubmit = async (data: FormData) => {
    const companyId = data.companyId || selectedCompanyId
    if (!companyId) return
    if (editingEmployee) {
      await apiFetch(`/companies/${editingEmployee.companyId}/employees/${editingEmployee.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      })
      toast.success('Funcionário atualizado')
      setEditingEmployee(null)
    } else {
      await apiFetch(`/companies/${companyId}/employees`, {
        method: 'POST',
        body: JSON.stringify(data)
      })
      toast.success('Funcionário cadastrado')
      setSelectedCompanyId(companyId)
    }
    await qc.invalidateQueries({ queryKey: ['employees', editingEmployee?.companyId ?? companyId] })
    await qc.invalidateQueries({ queryKey: ['employees', companyId] })
    reset({
      companyId,
      fullName: '',
      cpf: '',
      roleTitle: '',
      baseSalary: '',
      admissionDateBr: ''
    })
  }

  const openDeleteEmployeeConfirm = (e: Employee) => setDeleteEmployeeConfirm(e)

  const handleDelete = async () => {
    if (!selectedCompanyId || !deleteEmployeeConfirm) return
    const e = deleteEmployeeConfirm
    setDeleteEmployeeConfirm(null)
    await apiFetch(`/companies/${selectedCompanyId}/employees/${e.id}`, { method: 'DELETE' })
    toast.success('Funcionário excluído')
    await qc.invalidateQueries({ queryKey: ['employees', selectedCompanyId] })
  }

  if (!selectedCompanyId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700">
        Selecione uma empresa no topo para visualizar/cadastrar funcionários.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ConfirmModal
        open={!!deleteEmployeeConfirm}
        title="Excluir funcionário"
        message={
          deleteEmployeeConfirm
            ? `Excluir o funcionário "${deleteEmployeeConfirm.fullName}"? Ele será marcado como inativo.`
            : ''
        }
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteEmployeeConfirm(null)}
      />
      <div>
        <div className="text-xl font-semibold text-slate-900">Funcionários</div>
        <div className="mt-1 text-sm text-slate-500">Cadastro e gestão por empresa.</div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900">Lista</div>
            <input
              type="text"
              placeholder="Buscar funcionário..."
              className="h-9 w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="mt-4 space-y-2">
            {filteredEmployees.map((e) => (
              <div key={e.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">{e.fullName}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {e.cpfMasked} • {e.roleTitle} • Admissão: {e.admissionDateBr}
                    </div>
                    <div className="mt-2 text-xs text-slate-600">
                      Salário base: <span className="font-medium">R$ {e.baseSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span
                      className={[
                        'rounded-full px-3 py-1 text-xs font-semibold',
                        e.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      ].join(' ')}
                    >
                      {e.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                    <button
                      type="button"
                      className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                      onClick={() => setEditingEmployee(e)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                      onClick={() => openDeleteEmployeeConfirm(e)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {listQ.isLoading && <div className="text-sm text-slate-500">Carregando…</div>}
            {listQ.isError && (
              <div className="text-sm text-red-600">Falha ao carregar funcionários.</div>
            )}
            {!listQ.isLoading && !listQ.isError && filteredEmployees.length === 0 && (
              <div className="text-sm text-slate-500">
                {filter ? 'Nenhum funcionário encontrado com o filtro.' : 'Nenhum funcionário cadastrado.'}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-900">
            {editingEmployee ? 'Editar funcionário' : 'Cadastrar funcionário'}
          </div>
          {editingEmployee && (
            <button
              type="button"
              className="mt-2 text-xs text-slate-500 underline"
              onClick={() => {
                setEditingEmployee(null)
                reset({
                  companyId: selectedCompanyId ?? '',
                  fullName: '',
                  cpf: '',
                  roleTitle: '',
                  baseSalary: '',
                  admissionDateBr: '',
                })
              }}
            >
              Cancelar edição
            </button>
          )}
          <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label className="text-sm font-medium text-slate-700">Empresa do funcionário</label>
              <select
                className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                disabled={!!editingEmployee}
                {...register('companyId')}
                onChange={(e) => {
                  setValue('companyId', e.target.value)
                  if (!editingEmployee) setSelectedCompanyId(e.target.value || null)
                }}
              >
                <option value="">Selecione a empresa</option>
                {(companiesQ.data?.companies ?? []).map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.tradeName}
                  </option>
                ))}
              </select>
              {errors.companyId && (
                <div className="mt-1 text-xs text-red-600">{errors.companyId.message}</div>
              )}
              {editingEmployee && (
                <div className="mt-1 text-xs text-slate-500">
                  Para mudar um funcionário de empresa, exclua/inative e cadastre novamente na empresa correta.
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Nome completo</label>
              <input
                className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                {...register('fullName')}
              />
              {errors.fullName && (
                <div className="mt-1 text-xs text-red-600">{errors.fullName.message}</div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">CPF</label>
                <IMaskInput
                  mask="000.000.000-00"
                  value={watch('cpf') ?? ''}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  onAccept={(v) => setValue('cpf', String(v))}
                />
                {errors.cpf && (
                  <div className="mt-1 text-xs text-red-600">{errors.cpf.message}</div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Cargo</label>
                <input
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  {...register('roleTitle')}
                />
                {errors.roleTitle && (
                  <div className="mt-1 text-xs text-red-600">{errors.roleTitle.message}</div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Salário base (R$)</label>
                <IMaskInput
                  mask={Number}
                  radix=","
                  thousandsSeparator="."
                  scale={2}
                  padFractionalZeros
                  normalizeZeros
                  value={watch('baseSalary') ?? ''}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  onAccept={(v) => setValue('baseSalary', String(v))}
                />
                {errors.baseSalary && (
                  <div className="mt-1 text-xs text-red-600">{errors.baseSalary.message}</div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Data de admissão</label>
                <IMaskInput
                  mask="00/00/0000"
                  value={watch('admissionDateBr') ?? ''}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  onAccept={(v) => setValue('admissionDateBr', String(v))}
                />
                {errors.admissionDateBr && (
                  <div className="mt-1 text-xs text-red-600">{errors.admissionDateBr.message}</div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700">
              Cadastro de funcionário usado para <span className="font-semibold">cálculo de rescisão</span> (salário base e data de admissão).
            </div>

            <button
              disabled={isSubmitting}
              className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              type="submit"
            >
              {isSubmitting ? 'Salvando…' : editingEmployee ? 'Atualizar' : 'Salvar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
